/**
 * The pad, in a menu — a second, deliberately menu-scoped consumer of
 * `navigator.getGamepads()`.
 *
 * Issue #78. A gamepad works in a run (#67) and cannot touch a panel, so on a
 * controller-first setup the game is unstartable. There are two independent
 * reasons and this file is the answer to one of them; the other is that
 * `src/ui/uiRoot.ts` binds key codes directly, which is fixed by wiring this in
 * beside those bindings rather than by replacing them.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS A SECOND POLL AND NOT A RELAXATION OF THE RUN-TIME GATE
 * ---------------------------------------------------------------------------
 *
 * `Game.onScreenChange` calls `input.setEnabled(s === 'none')`, so `Input` is
 * deaf for exactly as long as a panel is open. That gate is **correct**: it is
 * what stops a held button firing into the run underneath an open menu, one of
 * the four defects #67 fixed, and lifting it to let the pad reach the UI would
 * reintroduce that defect in order to fix this one.
 *
 * So the deafness is not the problem — the problem is that nothing else was
 * listening. This listens. It runs *only* while a panel is open, which makes
 * the two consumers mutually exclusive by construction rather than by
 * convention: at any instant exactly one of `Input` and `MenuPad` is reading
 * the pad, and neither has to know about the other. That is also why button 0
 * being `jump` and button 1 being `offAttack` in `DEFAULT_PAD_BINDINGS` is not
 * a conflict with `confirm`/`back` here.
 *
 * ---------------------------------------------------------------------------
 * THE THREE DECISIONS #78 ASKED FOR, ANSWERED
 * ---------------------------------------------------------------------------
 *
 * **1. Repeat cadence, and where the duration comes from.**
 *
 * The no-wall-clock invariant is enforced by `tests/sim.test.ts`'s "no
 * simulation system reads a wall clock", and its glob is `../src/sim/*.ts`.
 * The UI layer is **outside** that boundary — `uiRoot.ts` already calls
 * `performance.now()` for the radial hold — so a timer here would be legal.
 *
 * It is still the wrong tool, and this uses the `dt` it is handed instead:
 *
 *   - `UIRoot.update(vm, dt)` is already called every frame *including while
 *     paused* (`Game.frame` runs `render(dt)` outside its `if (!this.paused)`
 *     block, and `render` is what calls `ui.update`). The duration is therefore
 *     already in the room; reaching for a clock would be importing a second
 *     source of time next to one that works.
 *   - An injected `dt` is testable by *feeding it*. A `performance.now()`
 *     cadence can only be tested by monkey-patching the clock, which this repo
 *     has had to do once already (`tests/sim.test.ts`) and would rather not do
 *     twice.
 *   - It has no timer to leak. `UIRoot.destroy()` needs no new line, and a
 *     backgrounded tab stops repeating because `requestAnimationFrame` stops —
 *     a menu that keeps scrolling while nobody is looking is a defect, not a
 *     feature.
 *
 * The numbers are `MENU_REPEAT_DELAY` and `MENU_REPEAT_INTERVAL`, argued at
 * their declarations and exported so the test asserts against the constants
 * rather than transcribing them — the same reasoning that exports
 * `PAD_DEADZONE`.
 *
 * **2. Which button is "back".**
 *
 * `uiRoot` uses Escape for two different verbs: with no panel open it *opens*
 * pause, and with one open it *closes* to none. A pad cannot overload one
 * button the way a keyboard got away with overloading one key, because the
 * player cannot see the binding. So the two verbs are split:
 *
 *   - **B / circle (button 1) is `back`, always, and only ever means "one level
 *     shallower".** It is the platform-conventional cancel in every layout the
 *     W3C standard mapping normalises. It never opens anything.
 *   - **Start (button 9) is `back` too, at the top level of a panel** — Start
 *     opened the pause screen and Start closes it, which is what every player
 *     already expects from a pause button. Below that it is the same "one level
 *     shallower" verb, so the two never disagree.
 *
 * `A`/cross (0) is `confirm` for the same conventional reason, and `X`/square
 * (2) is `altConfirm`: the loadout's Enter already reads `shiftKey` to choose
 * which hand a weapon goes to, so the off-hand equip needs a second button
 * rather than a second code path.
 *
 * **3. Does the d-pad navigate as well as the stick? Yes.**
 *
 * The same argument #67 used to bind 12–15 in the first place: a pad with a
 * worn or dead left stick — which is most second-hand hardware, and the
 * standard failure of a drifting Joy-Con — must still be able to reach the
 * whole UI. The d-pad is in fact the *better* menu device, because it is
 * already discrete and needs no deadzone at all.
 *
 * Both feed the **same** four direction intents and therefore the same repeat
 * clock, so holding d-pad-down and stick-down together produces one cadence
 * rather than two interleaved ones. That is not a nicety: two sources driving
 * one action independently is precisely the shape of the bug where buttons 6
 * and 7 were both `dash` and cancelled each other every frame.
 *
 * ---------------------------------------------------------------------------
 * THE STICK IS QUANTISED HERE, UNLIKE IN A RUN
 * ---------------------------------------------------------------------------
 *
 * `Input.axis` stays continuous because movement reads it as an analogue
 * magnitude. A list has no analogue magnitude and no diagonal: pushing the
 * stick down and 15° right has to mean *down*, not down-and-right, or a
 * deliberate move down the pause menu also flips a tab. So the menu reduces the
 * stick to **one of four**, by dominant component, past the same
 * `getPadDeadzone()` value the run uses — one setting, two quantisers, each
 * argued where it lives.
 */

import { DEFAULT_PAD_BINDINGS, getPadDeadzone } from '../core/input';
import type { AppScene } from './scene';
import type { Screen } from './viewModels';

/**
 * What a menu can be told to do. Deliberately *not* `Action`: an `Action` is a
 * verb in the world ("jump", "parry"), and no menu has one of those. Sharing
 * the enum would have meant a menu reading `jump` and calling it confirm, which
 * is the kind of pun that survives right up until somebody rebinds jump.
 */
export type MenuIntent =
  | 'up'
  | 'down'
  | 'left'
  | 'right'
  | 'confirm'
  | 'altConfirm'
  | 'back'
  | 'tabPrev'
  | 'tabNext'
  /**
   * Y / triangle — issue #193. The one verb with a meaning on exactly one
   * screen: `runSetup`'s reroll. Every other panel's `menuCommandFor` falls
   * through its `default: null`, so pressing Y in the loadout or the pause
   * menu does nothing, the same silence the other seven buttons `MENU_PAD_
   * BUTTONS` never binds already produce there.
   */
  | 'reroll';

/**
 * Pad button index -> menu intent, in the W3C standard mapping.
 *
 * Sparse on purpose. A menu binding for every button would mean a player
 * resting a thumb on a face button while reading a panel triggers something,
 * and the buttons left out here (6, 7, 8, 10, 11) are exactly the ones with
 * no menu verb to give them. `tests/menuPad.test.ts` drives every entry and
 * asserts the intent comes out, the same two-way shape `DEFAULT_PAD_BINDINGS`
 * gets in `tests/gamepad.test.ts`: this project's recurring defect is an
 * authored table nothing reads.
 *
 * Button 3 (Y / triangle) was one of the silent ones until issue #193 gave it
 * `reroll` — the seed reroll had no pad path at all, unlike the two footer
 * buttons (`shellCommandFor`'s `back`/`confirm`→`Escape`/`Enter` already
 * reached those). Y rather than a modifier combination: nothing else on the
 * pad uses it while a shell scene owns the token (`DEFAULT_PAD_BINDINGS`'
 * button 3 is `interact`, live only in a run, when `Input` — not `MenuPad` —
 * owns the pad), so there is no chord to invent and no existing meaning to
 * take away.
 */
export const MENU_PAD_BUTTONS: Readonly<Record<number, MenuIntent>> = {
  0: 'confirm', // A / cross
  1: 'back', // B / circle
  2: 'altConfirm', // X / square — the loadout's shift+Enter, as a button
  3: 'reroll', // Y / triangle — runSetup only; see above
  4: 'tabPrev', // LB / L1
  5: 'tabNext', // RB / R1
  9: 'back', // Start / Options — the button that opened it closes it
  12: 'up',
  13: 'down',
  14: 'left',
  15: 'right',
};

/**
 * Seconds a direction must be held before it starts repeating.
 *
 * 0.42 s. It has to be comfortably longer than a deliberate flick-and-return,
 * which a human does in roughly 0.12–0.20 s, or every single-step move down a
 * list turns into two. It also has to be short enough that a player who *wants*
 * to travel does not think the pad has stopped working. The platform defaults
 * bracket it — Windows' key-repeat delay ranges 0.25–1.0 s and macOS' 0.225–1.0
 * s — and console menus cluster at 0.4–0.5 s.
 */
export const MENU_REPEAT_DELAY = 0.42;

/**
 * Seconds between repeats once repeating, i.e. about 7 moves a second.
 *
 * Fast enough to cross the codex's longest list in a couple of seconds, slow
 * enough to stop on the row you meant in a six-row pause menu. The two
 * constants are exported together because the pair is the feel; either one on
 * its own says nothing.
 */
export const MENU_REPEAT_INTERVAL = 0.14;

/** The four that auto-repeat. Confirm and back deliberately do not — see below. */
const DIRECTIONS: readonly MenuIntent[] = ['up', 'down', 'left', 'right'];

/** A pad-shaped thing. Narrow on purpose so a test can build one by hand. */
export interface PadLike {
  index?: number;
  connected?: boolean;
  buttons: ReadonlyArray<{ pressed?: boolean; value?: number } | null | undefined>;
  axes: ReadonlyArray<number>;
}

/** The source of pads. Injected so the test does not have to fake `navigator`. */
export type PadSource = () => ReadonlyArray<PadLike | null>;

/**
 * Analogue triggers that report `value` without ever setting `pressed`.
 * The same threshold and the same reason as `input.ts`'s — some pads and some
 * HID fallbacks only move `value`.
 */
const TRIGGER_THRESHOLD = 0.5;

const defaultSource: PadSource = () => {
  if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return [];
  return navigator.getGamepads() as ReadonlyArray<PadLike | null>;
};

/**
 * The one pump allowed to read the pad, or null for "no one has claimed it".
 *
 * ---------------------------------------------------------------------------
 * WHY A MODULE-LEVEL TOKEN, WHICH IS NORMALLY THE WRONG ANSWER
 * ---------------------------------------------------------------------------
 *
 * There are now two `MenuPad`s in a running game and there is a window in which
 * both are live: opening Settings from the pause menu does *not* tear the run
 * down (`main.ts`'s `exit`), so `Game` keeps rendering, `UIRoot.update` keeps
 * running, and `UIRoot`'s screen is still `'pause'` underneath a full-screen
 * settings dialog. Without a token, one press of B would close settings *and*
 * resume the run, and every stick flick would silently walk a focus ring the
 * player cannot see. That is the same class of defect as the `Input`-vs-menu
 * overlap the whole file exists to prevent, one layer up.
 *
 * The alternative was for the shell to reach into the live `Game`, through a
 * private `ui` field, to switch the other pump off — coupling two screens
 * through a third object, and a `game.ts` change, to express "there is one
 * physical pad". The token says that directly. It is global for exactly the
 * reason `navigator.getGamepads()` is global: the resource is.
 *
 * Default `null` is load-bearing. Nothing claims until something needs to
 * exclude, so `UIRoot` behaves as it did before this existed and a claimant
 * that forgets to release is the only way to break it — which is why claiming
 * is not a public verb anyone calls by hand: `ShellPad` claims on start and
 * releases on stop, and a stopped pump cannot hold the pad.
 */
let padOwner: MenuPad | null = null;

/**
 * The pump.
 *
 * Stateless between screens and stateful within one: `arm()` adopts whatever
 * the pad is currently holding *without emitting*, which is what stops the
 * button that opened a panel from immediately confirming inside it. That is the
 * menu-side twin of the rule `Input.pollGamepad` already follows — a press
 * swallowed while disabled must not fire later.
 */
export class MenuPad {
  private source: PadSource;
  private padIndex = -1;
  /** Intents asserted on the previous poll — the whole of the edge detection. */
  private prev = new Set<MenuIntent>();
  /** Seconds until the next repeat, per held direction. */
  private until = new Map<MenuIntent, number>();

  constructor(source: PadSource = defaultSource) {
    this.source = source;
  }

  /**
   * Adopt the pad's current state as the baseline, emitting nothing.
   *
   * Call on every screen transition. Opening the pause menu with Start, or the
   * loadout with Back, leaves that button physically down for several frames;
   * without this the first poll inside the panel sees a rising edge that never
   * happened and closes it again.
   */
  arm(): void {
    this.prev = this.assertedNow();
    this.until.clear();
    for (const d of this.prev) {
      if (DIRECTIONS.includes(d)) this.until.set(d, MENU_REPEAT_DELAY);
    }
  }

  /** Forget everything. Nothing is held until it is pressed again. */
  reset(): void {
    this.prev.clear();
    this.until.clear();
    this.padIndex = -1;
    // A pump being torn down must not keep the pad away from the one that
    // outlives it. `UIRoot.destroy()` already calls this.
    this.release();
  }

  /**
   * Take sole ownership of the pad, and adopt what it is holding.
   *
   * Every other `MenuPad` goes silent until `release()`. The `arm()` is not a
   * separate step a caller could forget: the button that got the player *to*
   * this pump — A on the pause menu's Settings row — is still physically down,
   * and a claim without an arm would activate whatever the new screen focuses.
   */
  claim(): void {
    padOwner = this;
    this.arm();
  }

  /** Give the pad back. A no-op unless this pump is the one holding it. */
  release(): void {
    if (padOwner === this) padOwner = null;
  }

  /** True while this pump is the one the token names. Reporting and tests. */
  get owning(): boolean {
    return padOwner === this;
  }

  /**
   * One frame. Returns the intents that fired, in a stable order.
   *
   * Rising edges fire for every intent. Directions *also* fire on the repeat
   * schedule while held; `confirm`, `altConfirm`, `back`, `tabPrev` and
   * `tabNext` never repeat, because a repeating confirm activates whatever the
   * first activation navigated to — which is how a held button walks a player
   * through three screens they never saw.
   */
  poll(dt: number): MenuIntent[] {
    // Somebody else holds the pad. Arm rather than merely return: a suppressed
    // pump that froze its `prev` would see a phantom rising edge for every
    // button pressed during the suppression the instant it got the pad back —
    // the B that closed the settings screen would then also resume the run.
    // Arming every suppressed frame means the hand-back is always silent.
    if (padOwner !== null && padOwner !== this) {
      this.arm();
      return [];
    }

    const now = this.assertedNow();
    const out: MenuIntent[] = [];

    for (const intent of now) {
      if (this.prev.has(intent)) continue;
      out.push(intent);
      if (DIRECTIONS.includes(intent)) this.until.set(intent, MENU_REPEAT_DELAY);
    }

    for (const d of DIRECTIONS) {
      if (!now.has(d)) {
        this.until.delete(d);
        continue;
      }
      if (!this.prev.has(d)) continue; // just fired above; do not also age it
      let left = (this.until.get(d) ?? MENU_REPEAT_DELAY) - dt;
      // A single frame longer than the interval emits once, not a burst: a
      // 250 ms hitch must not dump four moves into the list at once.
      if (left <= 0) {
        out.push(d);
        left = MENU_REPEAT_INTERVAL;
      }
      this.until.set(d, left);
    }

    this.prev = now;
    return out;
  }

  /** True while some pad is visible to the poll. Used only for reporting. */
  get connected(): boolean {
    return this.activePad() !== null;
  }

  /**
   * Every raw button index currently pressed, untranslated — issue #191.
   *
   * `assertedNow` only ever looks at `MENU_PAD_BUTTONS`' keys, because a menu
   * has no verb for the other buttons. The capture-gamepad-button settings row
   * needs exactly the opposite: it is capturing a button for `Input`'s action
   * table, which has to be able to land on any index `DEFAULT_PAD_BINDINGS`
   * could, not just the ones this file gives a navigation meaning to. Same
   * ownership guard as `poll()`, so a suppressed pump cannot report a press
   * that belongs to whoever currently holds the token.
   */
  rawButtons(): Set<number> {
    const out = new Set<number>();
    if (padOwner !== null && padOwner !== this) return out;
    const pad = this.activePad();
    if (!pad) return out;
    for (let i = 0; i < pad.buttons.length; i++) {
      const b = pad.buttons[i];
      if (!b) continue;
      if (b.pressed || (b.value ?? 0) > TRIGGER_THRESHOLD) out.add(i);
    }
    return out;
  }

  /**
   * The pad being read, discovering or re-discovering one as needed.
   *
   * Same policy as `Input.activePad` and for the same reasons: the API is
   * poll-based, `gamepadconnected` is not guaranteed to arrive, a slot that has
   * emptied is a disconnect on its own, and the first pad wins so a second
   * controller joining a couch session cannot take the menu away from player
   * one. Duplicated rather than shared because sharing it would mean this file
   * and `Input` agreeing on *which* pad through a mutable field, and two
   * consumers writing one index is the coupling the whole design avoids.
   */
  private activePad(): PadLike | null {
    const pads = this.source();
    if (this.padIndex >= 0) {
      const held = pads[this.padIndex];
      if (held && held.connected !== false) return held;
      this.padIndex = -1;
    }
    for (let i = 0; i < pads.length; i++) {
      const p = pads[i];
      if (!p || p.connected === false) continue;
      this.padIndex = typeof p.index === 'number' ? p.index : i;
      return p;
    }
    return null;
  }

  /** Every intent the pad is asserting right now, buttons and stick together. */
  private assertedNow(): Set<MenuIntent> {
    const out = new Set<MenuIntent>();
    const pad = this.activePad();
    if (!pad) return out;

    for (const key in MENU_PAD_BUTTONS) {
      const b = pad.buttons[Number(key)];
      if (!b) continue;
      if (b.pressed || (b.value ?? 0) > TRIGGER_THRESHOLD) out.add(MENU_PAD_BUTTONS[Number(key)]);
    }

    const dir = stickDirection(pad.axes[0] ?? 0, pad.axes[1] ?? 0);
    if (dir) out.add(dir);
    return out;
  }
}

/**
 * The left stick, as one of four directions or none.
 *
 * Exported for the test, which sweeps the circle rather than transcribing the
 * three branches below. `+y` is down, matching the world's screen-space
 * convention and the Gamepad API's.
 *
 * Ties go to **vertical**. A list is vertical, the stick's diagonal rest
 * position after a hard down-push tends to carry a few degrees of x, and a
 * dead-even 45° push is a player who has not decided — so the axis that
 * matters wins it.
 *
 * Reads `getPadDeadzone()` rather than a constant — issue #192. The settings
 * screen's deadzone row has to reach this quantiser as well as `Input`'s, and
 * a `MenuPad` can be polling before any `Input` exists (the title screen has
 * no `Game`), so the shared value lives in `core/input.ts` and is read fresh
 * on every call rather than captured once.
 */
export function stickDirection(x: number, y: number): MenuIntent | null {
  if (Math.hypot(x, y) <= getPadDeadzone()) return null;
  if (Math.abs(x) > Math.abs(y)) return x > 0 ? 'right' : 'left';
  return y > 0 ? 'down' : 'up';
}

/**
 * The pad buttons that open a panel from inside a live run, and the `Action`
 * each already produces.
 *
 * This table is not read by `MenuPad` — the pump is deliberately inert while no
 * panel is open, because `Input` is enabled then and a second reader of the
 * same physical press is exactly the duplication #67 spent four bugs on. It is
 * here as the *other half of the story*, so the two halves are legible in one
 * place: opening a panel is `Game` consuming an `Action` that `Input` already
 * binds, and driving it is this file.
 *
 * `codex` is absent because `DEFAULT_PAD_BINDINGS` has no button for it —
 * sixteen buttons, seventeen actions, and that is the one recorded as left off.
 * The codex is reachable from the pause menu, which is why it is survivable.
 */
export const PAD_OPENS: Readonly<Record<number, 'inventory' | 'map' | 'pause'>> = {
  8: 'inventory', // Back / Share
  9: 'pause', // Start / Options
  11: 'map', // R3
};

// --------------------------------------------------------------- routing
//
// What an intent *means* depends on which panel is open, and that decision is a
// pure function of two enums. It lives here rather than inside `UIRoot` for the
// reason `viewModels.ts` exists at all: vitest runs this suite in `node`, the
// panels need a document, and a navigation policy buried in a DOM class is a
// policy no test in this repo can reach. Split out, the whole table is asserted
// directly and `UIRoot` is left with nothing but the execution.

/**
 * One thing a menu does in response to one intent.
 *
 * `key` is the escape hatch and it is deliberate: `LoadoutPanel.handleKey` and
 * `CodexPanel.handleKey` are the existing, tested vocabulary those panels speak,
 * and re-expressing list navigation as a second API into them would be two ways
 * to move one cursor. The pad therefore speaks the panels' own language — it is
 * the *binding* that was missing, not the behaviour.
 */
export type MenuCommand =
  | { kind: 'screen'; to: Screen }
  /** Close the panel *and* tell the integrator to unpause. Only from pause. */
  | { kind: 'resume' }
  /** Hand a keystroke to the open panel's `handleKey`. */
  | { kind: 'key'; key: string; shift: boolean }
  /** Move the focus ring among the open panel's own controls. */
  | { kind: 'focus'; delta: number }
  /** Activate whatever the focus ring is on. */
  | { kind: 'activate' }
  /** Cycle the open panel's tab strip. */
  | { kind: 'tab'; delta: number };

/**
 * Intent + open screen -> what to do, or null for "this means nothing here".
 *
 * The three screens are genuinely three different shapes and the table says so
 * rather than pretending otherwise:
 *
 *   - **pause** is a column of buttons, so it is driven by the focus ring. Up
 *     and down move it, confirm clicks it, and `back` resumes — the one place
 *     `back` does more than close, because closing the pause screen *is*
 *     resuming and leaving the run paused with no menu on screen would be a
 *     softlock with no visible cause.
 *   - **loadout** and **codex** are list panels with a tab strip and their own
 *     `handleKey`, so they get arrow keys, Enter, and shoulder-button tabs.
 *   - **map** is a picture. Nothing navigates inside it, so only `back` does
 *     anything, and pretending otherwise would give the player a stick that
 *     silently does nothing.
 *   - **gameover** is a column of buttons like `pause`, and differs from it in
 *     exactly one way that has to be written down: **`back` does nothing.**
 *     Every other screen here is something the player opened and can therefore
 *     close, and the fallthrough for an unlisted screen is `{ to: 'none' }` —
 *     so a `gameover` that was not named in this function would be dismissable
 *     with B, leaving a dead player standing in a live world with a HUD. The
 *     run is over; the only ways out are the screen's own two rows.
 *
 * `none` returns null for everything, which is the load-bearing half: the pump
 * is inert while no panel is open, because `Input` is enabled then and owns the
 * pad. Opening a panel is `Game`'s job through `PAD_OPENS`.
 */
export function menuCommandFor(intent: MenuIntent, screen: Screen): MenuCommand | null {
  if (screen === 'none') return null;

  // Before the `back` branch, so the death screen cannot be closed by it.
  if (screen === 'gameover') {
    switch (intent) {
      case 'up':
        return { kind: 'focus', delta: -1 };
      case 'down':
        return { kind: 'focus', delta: 1 };
      case 'confirm':
        return { kind: 'activate' };
      default:
        return null;
    }
  }

  if (intent === 'back') {
    return screen === 'pause' ? { kind: 'resume' } : { kind: 'screen', to: 'none' };
  }

  if (screen === 'map') return null;

  if (screen === 'pause') {
    switch (intent) {
      case 'up':
        return { kind: 'focus', delta: -1 };
      case 'down':
        return { kind: 'focus', delta: 1 };
      case 'confirm':
        return { kind: 'activate' };
      default:
        // No tab strip and no off-hand equip on the pause screen. Left/right
        // and the shoulders are silent rather than aliased onto up/down: a
        // control that does something *else* is worse than one that does
        // nothing, because the player cannot tell it apart from a mistake.
        return null;
    }
  }

  // loadout and codex — the two list panels.
  switch (intent) {
    case 'up':
      return { kind: 'key', key: 'ArrowUp', shift: false };
    case 'down':
      return { kind: 'key', key: 'ArrowDown', shift: false };
    // Left/right page rather than switching tabs. The shoulders own the tab
    // strip (that is what shoulders are for), and a list long enough to need a
    // pad is a list long enough to need paging.
    case 'left':
      return { kind: 'key', key: 'PageUp', shift: false };
    case 'right':
      return { kind: 'key', key: 'PageDown', shift: false };
    case 'confirm':
      return { kind: 'key', key: 'Enter', shift: false };
    case 'altConfirm':
      // The off hand. `LoadoutPanel.handleKey` already reads `shiftKey` on
      // Enter to choose the hand, so this is the existing branch reached by a
      // button instead of by a modifier the pad does not have.
      return { kind: 'key', key: 'Enter', shift: true };
    case 'tabPrev':
      return { kind: 'tab', delta: -1 };
    case 'tabNext':
      return { kind: 'tab', delta: 1 };
    default:
      return null;
  }
}

/**
 * How many tabs each list panel has, for the shoulder buttons to cycle through.
 *
 * Four and four: `LoadoutPanel` is weapons/modules/loadouts/craft and
 * `CodexPanel` is materials/reactions/enemies/tracks, and both already select
 * by the number keys `1`–`4`. Named here rather than hard-coded at the call
 * site so the day a fifth tab lands, the shoulder button reaches it by editing
 * one number that a test is watching.
 */
export const MENU_TABS: Readonly<Record<'loadout' | 'codex', number>> = {
  loadout: 4,
  codex: 4,
};

/**
 * Assert that `PAD_OPENS` agrees with the run-time table it describes.
 *
 * A helper rather than a comment because the failure it guards is silent: if
 * `DEFAULT_PAD_BINDINGS` ever moves `map` off button 11, the sentence above
 * becomes false and nothing anywhere notices. The test calls this.
 */
export function padOpensMatchesInputTable(): string[] {
  const wrong: string[] = [];
  for (const key in PAD_OPENS) {
    const btn = Number(key);
    const want = PAD_OPENS[btn];
    const got = DEFAULT_PAD_BINDINGS[btn];
    if (got !== want) wrong.push(`button ${btn}: PAD_OPENS says ${want}, input.ts says ${got}`);
  }
  return wrong;
}

// ============================================================= the shell
//
// Everything above drives a panel *inside* a run. The title screen, the
// archetype select and the settings screen are not panels — they are the shell
// scenes in `title.ts`, `runSetup.ts` and `settings.ts`, mounted by `main.ts`,
// and until this section none of them read a pad at all. That is the half of
// #78 that made the headline claim true: a controller could play a run it had
// no way to start.
//
// The pieces below are the reusable half of `MenuPad` pointed at those three,
// and they are here rather than in the screens for the reason `menuCommandFor`
// is here: vitest runs in `node`, the screens need a document, and a policy
// buried in a DOM class is a policy no test in this repo can reach.

/** The three screens that exist before, or instead of, a `Game`. */
export type ShellScene = 'title' | 'runSetup' | 'settings';

/**
 * Which shell scenes own the pad, as a total function of `AppScene`.
 *
 * `Record<AppScene, …>` rather than a lookup with a default, so adding a sixth
 * scene to `ui/scene.ts` is a compile error here instead of a screen that
 * silently ignores a controller — which is the exact failure this issue is.
 *
 * `prologue` and `playing` map to null because in both a `Game` exists, `Input`
 * owns the pad for the run and `UIRoot`'s pump owns it for the panels. A third
 * reader would be the duplication the top of this file spends four defects on.
 */
export const SHELL_PAD_SCENE: Readonly<Record<AppScene, ShellScene | null>> = {
  title: 'title',
  runSetup: 'runSetup',
  settings: 'settings',
  prologue: null,
  playing: null,
};

/**
 * One thing a shell screen is told to do.
 *
 * `key` dominates for the same reason `MenuCommand.key` does: each of the three
 * screens already has a keyboard handler that is its complete, tested
 * navigation vocabulary, and re-expressing that as a second API would be two
 * ways to move one cursor. The pad speaks the screen's own language.
 *
 * `group` exists because `runSetup` is the one screen where it cannot. Its six
 * sections are switched with **Tab**, and Tab works there by *not* being
 * handled — the browser moves focus and a `focusin` listener notices. A
 * synthesised Tab is `isTrusted: false` and moves nothing, so the pad needs the
 * one verb the keyboard gets for free from the platform.
 *
 * `reroll` exists for the same reason `group` does — issue #193. `runSetup`'s
 * reroll button has no key of its own to synthesise (it is a plain `<button>`
 * driven by `onclick`/`onReroll`, not a keystroke `handleKey` answers to), so
 * the pad needs a third verb next to "send a key" and "move the section".
 */
export type ShellCommand =
  | { kind: 'key'; key: string }
  | { kind: 'group'; delta: number }
  | { kind: 'reroll' };

/**
 * Intent -> the key each shell screen already answers to.
 *
 * The three rows are three different screens and the table says so rather than
 * flattening them:
 *
 *   - **title** is a column of five with no depth. `back` is absent because
 *     there is nothing shallower than the title screen, and a B that quietly
 *     did nothing somewhere else would be worse than a B that does nothing
 *     here. Left/right and the shoulders are absent for the same reason.
 *   - **settings** is rows plus a value per row, which is exactly what a
 *     four-way d-pad is shaped like: up/down pick the row, left/right move the
 *     value, A opens the one row type that needs opening, B leaves.
 *   - **runSetup** is the odd one. Enter there means *begin the run* from
 *     anywhere on the screen, so binding A to Enter would start a run every
 *     time the player tried to tick an ability. A is therefore Space, which is
 *     that screen's own "choose the thing under the cursor", and X — the button
 *     `altConfirm` already exists for — is Enter. Begin is the deliberate
 *     press, which is the right way round for the only irreversible control on
 *     the screen.
 */
const SHELL_KEYS: Readonly<Record<ShellScene, Readonly<Partial<Record<MenuIntent, string>>>>> = {
  title: {
    up: 'ArrowUp',
    down: 'ArrowDown',
    confirm: 'Enter',
  },
  settings: {
    up: 'ArrowUp',
    down: 'ArrowDown',
    left: 'ArrowLeft',
    right: 'ArrowRight',
    confirm: 'Enter',
    back: 'Escape',
  },
  runSetup: {
    up: 'ArrowUp',
    down: 'ArrowDown',
    left: 'ArrowLeft',
    right: 'ArrowRight',
    confirm: ' ',
    altConfirm: 'Enter',
    back: 'Escape',
  },
};

/** Intent + shell screen -> what to do, or null for "nothing here". */
export function shellCommandFor(intent: MenuIntent, scene: ShellScene): ShellCommand | null {
  // The shoulders cycle `runSetup`'s sections, which is the same verb they have
  // inside a run (`menuCommandFor`'s `tab`) pointed at the same kind of thing.
  if (scene === 'runSetup') {
    if (intent === 'tabPrev') return { kind: 'group', delta: -1 };
    if (intent === 'tabNext') return { kind: 'group', delta: 1 };
    // Y — issue #193. Reroll is a button, not a key `SHELL_KEYS` can name; see
    // `ShellCommand`'s `reroll` case for why it is a third command kind.
    if (intent === 'reroll') return { kind: 'reroll' };
  }
  const key = SHELL_KEYS[scene][intent];
  return key === undefined ? null : { kind: 'key', key };
}

/**
 * A keystroke record for a screen's own `keydown` handler.
 *
 * A plain object cast rather than `new KeyboardEvent(...)`, the same choice and
 * the same reasons as `UIRoot.sendPanelKey`: a constructed event would be
 * untrusted and so would activate nothing anyway, and building one would make
 * every caller need a DOM constructor. `key` and `code` are set to the same
 * string because the three screens are split on which they read — `runSetup`
 * switches on `e.key`, `title` and `settings` on `e.code` — and for arrows,
 * Enter and Escape the two spellings coincide.
 */
export function padKeyEvent(key: string, shift = false): KeyboardEvent {
  return {
    key,
    code: key,
    shiftKey: shift,
    altKey: false,
    ctrlKey: false,
    metaKey: false,
    repeat: false,
    preventDefault: () => {},
    stopPropagation: () => {},
  } as unknown as KeyboardEvent;
}

/**
 * Where a frame comes from. Injected so a test can hand out frames by hand.
 */
export interface FrameSource {
  /** Ask for one callback, carrying a millisecond timestamp. Returns a handle. */
  request(cb: (nowMs: number) => void): number;
  cancel(handle: number): void;
}

/**
 * `requestAnimationFrame`, guarded so importing this file in `node` is safe.
 */
export const rafFrames: FrameSource = {
  request: (cb) => (typeof requestAnimationFrame === 'function' ? requestAnimationFrame(cb) : -1),
  cancel: (h) => {
    if (h >= 0 && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(h);
  },
};

/**
 * The shell's pad, and its frame source.
 *
 * ---------------------------------------------------------------------------
 * WHERE THE SHELL'S `dt` COMES FROM, AND WHY IT IS NOT THE GAME'S
 * ---------------------------------------------------------------------------
 *
 * `UIRoot`'s pump gets its `dt` for free because `Game.render(dt)` runs every
 * frame. At the title screen there is no `Game`, so the shell has to bring its
 * own frame source, and the three candidates were:
 *
 *   - **`setInterval`.** Rejected. It keeps firing in a backgrounded tab, so a
 *     resting stick would walk the menu while nobody is looking — the one
 *     property the in-run pump gets for nothing, thrown away.
 *   - **A `Game`-shaped loop the shell always runs.** Rejected. It would be
 *     live during a run as well, which is precisely the second reader this
 *     file's whole design is built to prevent.
 *   - **`requestAnimationFrame`, started and stopped with the scene.** Taken.
 *     It stops itself in a background tab, it hands out a real duration so the
 *     `dt` cadence above applies unchanged rather than needing a second timing
 *     model, and it is one object with one lifetime.
 *
 * The rejected-by-default failure of the third is a loop that outlives the
 * screen, so the lifetime is the invariant this class is built around rather
 * than a rule its callers are asked to follow:
 *
 *   - `setScene(null)` cancels the outstanding handle **and** releases the pad.
 *   - the callback re-schedules *before* delivering, so a command that changes
 *     the scene mid-frame cancels the frame it just booked rather than being
 *     followed by one.
 *   - a callback that was already in flight when the scene went away returns
 *     without re-scheduling.
 *
 * `tests/shellPad.test.ts` drives all three with a hand-cranked `FrameSource`
 * that would let a leaked loop be seen.
 */
export class ShellPad {
  private pad: MenuPad;
  private frames: FrameSource;
  private deliver: (cmd: ShellCommand, scene: ShellScene) => void;
  private scene: ShellScene | null = null;
  /** The booked frame, or -1. */
  private handle = -1;
  /** Timestamp of the last frame, or -1 for "the next one is the first". */
  private last = -1;
  /**
   * Set while the settings screen is capturing a pad button for an action
   * binding — issue #191. Non-null suppresses the normal `pad.poll(dt)` path:
   * a keyboard capture row already owns the keyboard outright while listening
   * (`SettingsScreen.captureKey`), and a pad capture row needs the same
   * exclusivity for the same reason — the very button being captured must not
   * *also* move the cursor or close the row.
   */
  private captureCb: ((button: number) => void) | null = null;
  /** Baseline for edge detection while `captureCb` is set. */
  private rawPrev = new Set<number>();
  private live = false;

  constructor(
    deliver: (cmd: ShellCommand, scene: ShellScene) => void,
    frames: FrameSource = rafFrames,
    source?: PadSource,
  ) {
    this.deliver = deliver;
    this.frames = frames;
    this.pad = new MenuPad(source);
  }

  /**
   * Point the pump at a screen, or at nothing.
   *
   * The single entry point on purpose: "which screen is up" is a fact the shell
   * already has, and deriving start/stop from it here is what stops the two
   * from drifting apart. `main.ts` calls this once, in `enter`, with
   * `SHELL_PAD_SCENE[scene]`.
   */
  setScene(next: ShellScene | null): void {
    if (next === this.scene) return;
    this.scene = next;
    if (next === null) {
      this.stop();
      return;
    }
    // Claim on every change, not only on the first, for the re-arm inside it.
    //
    // A shell transition does not have to come from the pad: the player can
    // click "Settings" with the mouse, or press Enter, with a thumb resting on
    // A. The pump's last poll then saw A *up*, and without the re-arm the first
    // poll on the new screen sees a rising edge that never happened and
    // activates whatever settings focuses. Coming from the pad this is a no-op,
    // because `poll` has already adopted the press — which is exactly the
    // property that makes it safe to do unconditionally.
    this.pad.claim();
    if (this.live) return;
    this.live = true;
    this.last = -1;
    this.schedule();
  }

  /** True while a frame is booked. The leak, if there is one, is visible here. */
  get running(): boolean {
    return this.live;
  }

  /** True while this pump, and not `UIRoot`'s, is the one reading the pad. */
  get owning(): boolean {
    return this.pad.owning;
  }

  private stop(): void {
    this.live = false;
    if (this.handle >= 0) {
      this.frames.cancel(this.handle);
      this.handle = -1;
    }
    this.captureCb = null;
    this.pad.release();
  }

  /**
   * Start listening for the next raw pad button press, across every index —
   * issue #191. `rawPrev` is seeded from the pad's *current* state, the same
   * "adopt without emitting" rule `MenuPad.arm` follows: the button that
   * opened the capture row (an A press translated to `confirm`) is very likely
   * still physically down on the first frame this runs, and without the
   * baseline it would read as the row's own answer.
   */
  startButtonCapture(cb: (button: number) => void): void {
    this.captureCb = cb;
    this.rawPrev = this.pad.rawButtons();
  }

  /** Stop listening. Idempotent — called whether or not a button ever arrived. */
  stopButtonCapture(): void {
    this.captureCb = null;
  }

  private schedule(): void {
    this.handle = this.frames.request((nowMs) => {
      this.handle = -1;
      // Booked before the scene went away. Do not re-schedule.
      if (!this.live) return;
      const dt = this.last < 0 ? 0 : Math.max(0, (nowMs - this.last) / 1000);
      this.last = nowMs;
      // Before delivering, so a `back` that ends the scene cancels this frame
      // instead of being followed by one nobody asked for.
      this.schedule();
      this.tick(dt);
    });
  }

  private tick(dt: number): void {
    if (this.captureCb) {
      const now = this.pad.rawButtons();
      let fired: number | null = null;
      for (const btn of now) {
        if (!this.rawPrev.has(btn)) {
          fired = btn;
          break;
        }
      }
      this.rawPrev = now;
      // The callback decides whether the button sticks (it may refuse a
      // reserved index and keep listening); either way this loop's own job —
      // routing raw presses instead of intents — is done once the row is no
      // longer capturing, so `captureCb` is re-checked rather than assumed.
      if (fired !== null) this.captureCb?.(fired);
      return;
    }
    for (const intent of this.pad.poll(dt)) {
      // Re-read per intent: a `back` earlier in this frame has already moved the
      // shell, and the next intent belongs to wherever the player now is.
      const scene = this.scene;
      if (scene === null) return;
      const cmd = shellCommandFor(intent, scene);
      if (cmd) this.deliver(cmd, scene);
    }
  }
}
